import { useState, useEffect } from 'react';
import { vehiclesAPI } from '../../../api';
import { useToast } from '../../../hooks/useToast';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Card, CardContent } from '../../../components/ui/Card';

const formatMileage = (val) => {
  if (val == null || val === '') return '—';
  return new Intl.NumberFormat('en-GB').format(val);
};

export default function MobileMileage() {
  const { success, error: showError, ToastContainer } = useToast();

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [recentLog, setRecentLog] = useState(null);
  const [formData, setFormData] = useState({
    logged_at: new Date().toISOString().slice(0, 10),
    start_mileage: '',
    end_mileage: '',
    notes: '',
    update_current_mileage: true,
  });

  const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        setFetchError('');
        const response = await vehiclesAPI.getAll({ limit: 100, is_active: true });
        const list = response?.vehicles || [];
        setVehicles(list);
        if (list.length > 0 && !selectedVehicleId) {
          setSelectedVehicleId(list[0].id);
        }
      } catch (err) {
        setFetchError(err.message || 'Failed to load vehicles');
        setVehicles([]);
      }
    };
    fetchVehicles();
  }, []);

  useEffect(() => {
    if (!selectedVehicleId) return;
    const fetchRecentLog = async () => {
      try {
        const logs = await vehiclesAPI.getMileageLogs(selectedVehicleId);
        const list = Array.isArray(logs) ? logs : logs?.logs || [];
        setRecentLog(list[0] ?? null);
      } catch {
        setRecentLog(null);
      }
    };
    fetchRecentLog();
  }, [selectedVehicleId]);

  useEffect(() => {
    if (!selectedVehicle) return;
    const current = selectedVehicle.current_mileage ?? recentLog?.end_mileage ?? '';
    setFormData((prev) => {
      if (prev.start_mileage !== '' || prev.end_mileage !== '') return prev;
      return { ...prev, start_mileage: String(current) };
    });
  }, [selectedVehicle?.id, selectedVehicle?.current_mileage, recentLog?.end_mileage]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleVehicleChange = (e) => {
    const id = e.target.value;
    setSelectedVehicleId(id);
    const v = vehicles.find((x) => x.id === id);
    const current = v?.current_mileage ?? null;
    setFormData((prev) => ({
      ...prev,
      start_mileage: current ?? '',
      end_mileage: '',
    }));
    setRecentLog(null);
  };

  const resetForm = () => {
    const current = selectedVehicle?.current_mileage ?? recentLog?.end_mileage;
    setFormData({
      logged_at: new Date().toISOString().slice(0, 10),
      start_mileage: current ?? '',
      end_mileage: '',
      notes: '',
      update_current_mileage: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVehicleId) {
      showError('No vehicle selected', 'Please select a vehicle');
      return;
    }
    const start = parseInt(formData.start_mileage, 10);
    const end = parseInt(formData.end_mileage, 10);
    if (isNaN(start) || isNaN(end) || end < start) {
      showError('Invalid mileage', 'End mileage must be greater than or equal to start mileage');
      return;
    }
    setLoading(true);
    try {
      await vehiclesAPI.addMileageLog(selectedVehicleId, {
        logged_at: new Date(formData.logged_at).toISOString(),
        start_mileage: start,
        end_mileage: end,
        notes: formData.notes?.trim() || null,
        update_current_mileage: formData.update_current_mileage,
      });
      success('Mileage logged', `${end - start} miles recorded`);
      resetForm();
      const logs = await vehiclesAPI.getMileageLogs(selectedVehicleId);
      const list = Array.isArray(logs) ? logs : logs?.logs || [];
      setRecentLog(list[0] ?? null);
    } catch (err) {
      showError('Failed to log mileage', err.message || 'Please try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 py-6 pb-8">
      <ToastContainer />

      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
        Log Mileage
      </h1>

      {fetchError && (
        <p className="text-sm text-red-600 dark:text-red-400 mb-4">{fetchError}</p>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <FormLabel required className="text-gray-700 dark:text-gray-300">
            Vehicle
          </FormLabel>
          <select
            name="vehicle"
            value={selectedVehicleId}
            onChange={handleVehicleChange}
            required
            className="min-h-[44px] w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select vehicle</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.reg_number || 'Unknown'}
                {v.current_mileage != null
                  ? ` (${formatMileage(v.current_mileage)} miles)`
                  : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <FormLabel required className="text-gray-700 dark:text-gray-300">
            Date of trip
          </FormLabel>
          <Input
            type="date"
            name="logged_at"
            value={formData.logged_at}
            onChange={handleChange}
            required
            className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel required className="text-gray-700 dark:text-gray-300">
              Start (miles)
            </FormLabel>
            <Input
              type="number"
              name="start_mileage"
              value={formData.start_mileage}
              onChange={handleChange}
              required
              min={0}
              placeholder="e.g. 45000"
              className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <FormLabel required className="text-gray-700 dark:text-gray-300">
              End (miles)
            </FormLabel>
            <Input
              type="number"
              name="end_mileage"
              value={formData.end_mileage}
              onChange={handleChange}
              required
              min={0}
              placeholder="e.g. 45120"
              className="min-h-[44px] border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>

        {formData.start_mileage !== '' && formData.end_mileage !== '' && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Business miles = {parseInt(formData.end_mileage, 10) - parseInt(formData.start_mileage, 10)} miles
          </p>
        )}

        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Notes (optional)</FormLabel>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={2}
            placeholder="Trip details..."
            className="min-h-[44px] w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-3 min-h-[44px]">
          <input
            type="checkbox"
            id="update_current_mileage"
            name="update_current_mileage"
            checked={formData.update_current_mileage}
            onChange={handleChange}
            className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <FormLabel htmlFor="update_current_mileage" className="mb-0 text-gray-700 dark:text-gray-300 cursor-pointer">
            Update vehicle current mileage to end reading
          </FormLabel>
        </div>

        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          className="w-full min-h-[44px]"
        >
          {loading ? 'Logging...' : 'Log Mileage'}
        </Button>
      </form>

      {selectedVehicleId && recentLog && (
        <Card className="mt-6 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-4">
            <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recent log
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {recentLog.logged_at
                ? new Date(recentLog.logged_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })
                : '—'}{' '}
              • {formatMileage(recentLog.start_mileage)} → {formatMileage(recentLog.end_mileage)} miles
              {recentLog.notes && (
                <>
                  <br />
                  <span className="text-gray-500 dark:text-gray-500">{recentLog.notes}</span>
                </>
              )}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
